/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useChatMessages } from '@ai-sdk-tools/store';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useIsMobile } from '@newfront-insurance/admin-ui';
import { isEmpty, isNotEmpty } from '@newfront-insurance/core';
import { CircleSpinnerInline, IconAdd2, SegmentedControlV2, useFuzzy } from '@newfront-insurance/core-ui';
import { cn, IconButtonAsButton, IconSearch, Input, NewfrontLogo } from '@newfront-insurance/core-ui/v2';
import { createContext, Fragment, type ReactNode, useContext, useMemo, useState } from 'react';

import { IconSidebar } from './icons';
import { OrphanedStreamsContainer } from './orphaned-streams-container';
import type { GetChatsByFilters } from '../../../queries/chats';
import { GET_CHATS_BY_FILTERS } from '../../../queries/chats';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { groupChatsByDate } from '../lib/chat-utils';
import { normalizeChatUuid } from '../lib/notification-helpers';

interface ChatSidebarContextValue {
  chatHistoryFilter: 'current-account' | 'all-accounts';
  setChatHistoryFilter: (value: 'current-account' | 'all-accounts') => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  searchResults: GetChatsByFilters;
  isLoadingChats: boolean;
}

const ChatSidebarContext = createContext<ChatSidebarContextValue | null>(null);

function useChatSidebarContext() {
  const context = useContext(ChatSidebarContext);
  if (!context) {
    throw new Error('ChatSidebar components must be used within ChatSidebar');
  }
  return context;
}

interface ChatSidebarProps {
  className?: string;
  children?: ReactNode;
}

/**
 * Chat sidebar component with history, search, and filtering.
 * Supports composable children for flexible layout customization.
 * If no children are provided, uses default composition for backward compatibility.
 *
 * @example Default usage (backward compatible):
 * ```tsx
 * <ChatSidebar />
 * ```
 *
 * @example Custom composition:
 * ```tsx
 * <ChatSidebar>
 *   <ChatSidebarNewChat />
 *   <ChatSidebarSearch />
 *   <ChatSidebarSettings>
 *     <MySettingsButton />
 *   </ChatSidebarSettings>
 *   <ChatSidebarFilterTabs />
 *   <ChatSidebarHistory />
 * </ChatSidebar>
 * ```
 *
 * Note: This component is now rendered inside ChatProvider (via NovaChatContainer),
 * so it can access store messages directly for the "New Chat" button.
 */
export function ChatSidebar({ className, children }: ChatSidebarProps) {
  const { isMobile } = useIsMobile();
  const { userUuid, accountUuid, setIsSidebarOpen, isSidebarOpen, isFullscreen, isStandaloneMode, appType } =
    useChatOverlayContext();

  const [chatHistoryFilter, setChatHistoryFilter] = useState<'current-account' | 'all-accounts'>(
    isEmpty(accountUuid) ? 'all-accounts' : 'current-account',
  );

  // Fetch chats for filtering and search
  const { data, networkStatus } = useQuery(GET_CHATS_BY_FILTERS, {
    variables: {
      userUuid,
      appType: appType ?? undefined,
    },
    skip: isEmpty(userUuid),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const { chatsList: chats = [] } = data ?? {};
  const isLoadingChats = networkStatus === NetworkStatus.loading && data === undefined;

  // Filter chats by account
  const chatsFilteredByAccount = useMemo(() => {
    return chatHistoryFilter === 'current-account' && isNotEmpty(accountUuid)
      ? [...chats].filter((chat) => chat.accountUuid === accountUuid)
      : [...chats];
  }, [chats, accountUuid, chatHistoryFilter]);

  // Apply fuzzy search - useFuzzy manages its own query state
  const fuzzySearch = useFuzzy(chatsFilteredByAccount, {
    keys: ['title', 'summary'],
  });

  const contextValue: ChatSidebarContextValue = useMemo(
    () => ({
      chatHistoryFilter,
      setChatHistoryFilter,
      searchQuery: fuzzySearch.query,
      setSearchQuery: fuzzySearch.search,
      searchResults: fuzzySearch.results,
      isLoadingChats,
    }),
    [chatHistoryFilter, fuzzySearch.query, fuzzySearch.results, fuzzySearch.search, isLoadingChats],
  );

  if (!isSidebarOpen) {
    return null;
  }

  // Default composition for backward compatibility
  const defaultChildren = (
    <>
      <ChatSidebarNewChat />
      <ChatSidebarSearch />
      <ChatSidebarFilterTabs />
      <ChatSidebarHistory />
    </>
  );

  return (
    <ChatSidebarContext.Provider value={contextValue}>
      {isSidebarOpen && isMobile && (
        <div
          className="fixed inset-0 z-[1299] bg-[#000]/70 opacity-50 backdrop-blur-xs"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      <div
        className={cn('absolute -top-px h-[calc(100%+2px)]', {
          'right-full z-[-1]': !isFullscreen,
          'z-overlay left-0': isFullscreen && !isMobile,
          'z-overlay right-0 left-0': isFullscreen && isMobile,
        })}
      >
        <div
          className={cn(
            'border-border/50 relative flex h-full w-60 shrink-0 flex-col gap-2 rounded-xs rounded-r-none border-y border-l',
            'bg-steel-50 dark:bg-steel-500',
            className,
          )}
        >
          <header
            className={cn(
              'border-border/50 flex h-14 w-60 shrink-0 items-center justify-between border-b px-6',
              'text-muted-foreground gap-2',
              'border-border/50 flex h-14 w-60 shrink-0 items-center border-b px-6',
              'text-muted-foreground',
              isMobile ? 'justify-between' : '',
            )}
          >
            <h1 className="text-muted-foreground font-semibold tracking-widest">Nova Assist</h1>
            {isMobile && (
              <IconButtonAsButton onClick={() => setIsSidebarOpen(false)} className="hover:bg-steel-200/20 shrink-0">
                <IconSidebar />
              </IconButtonAsButton>
            )}
          </header>
          <div className="flex min-h-0 flex-1 flex-col gap-2 pt-4">{children ?? defaultChildren}</div>
          {isStandaloneMode && (
            <div
              className={cn(
                'pointer-events-none absolute inset-x-0 bottom-0 h-20 rounded-b-xs shadow-lg',
                'from-steel-50 via-steel-50/95 bg-gradient-to-t to-transparent',
                'dark:from-steel-500 dark:via-steel-500/95 dark:to-transparent',
              )}
            >
              <div className="flex h-full items-end justify-center pb-3">
                <NewfrontLogo />
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Renderless container that recovers orphaned streams */}
      {chats.length > 0 && <OrphanedStreamsContainer chats={chats} />}
    </ChatSidebarContext.Provider>
  );
}

/**
 * New Chat button component for the sidebar.
 * Since ChatSidebar is now rendered inside ChatProvider (via NovaChatContainer),
 * this component can access store messages directly.
 */
export function ChatSidebarNewChat() {
  const { startNewChat, setIsSidebarOpen, setIsSettingsOpen } = useChatOverlayContext();
  const { isMobile } = useIsMobile();
  const messages = useChatMessages();

  // Use store messages to determine if button should be enabled
  // This ensures the button state reflects the actual current chat state
  const hasAnyMessages = messages.length > 0;

  return (
    <div className="flex shrink-0 flex-col gap-3 px-4">
      <MenuItem
        icon={<IconAdd2 color="#1C2A5A" size={16} />}
        className="font-semibold"
        iconClassName="rounded-full"
        iconStyle={{ background: 'linear-gradient(97deg, #A4F5FF 0%, #E7FC53 122.24%)' }}
        onClick={() => {
          startNewChat();
          setIsSettingsOpen(false); // Close settings when starting new chat

          if (isMobile) {
            setIsSidebarOpen(false);
          }
        }}
        disabled={!hasAnyMessages}
      >
        <span className="pl-1 text-[#1C2A5A]">New chat</span>
      </MenuItem>
    </div>
  );
}

/**
 * Search component for the sidebar.
 * Provides fuzzy search functionality for chat history.
 */
export function ChatSidebarSearch() {
  const { searchQuery, setSearchQuery } = useChatSidebarContext();

  return (
    <div className="flex shrink-0 flex-col gap-3 px-4">
      <MenuItem icon={<IconSearch className="text-steel-200 size-5" />} className="py-1">
        <Input
          wrapperClassName="h-7 border-0"
          inputClassName="bg-steel-50 px-1"
          placeholder="Search chats"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </MenuItem>
    </div>
  );
}

/**
 * Filter tabs component for the sidebar.
 * Allows filtering chat history by account (current account vs all accounts).
 */
export function ChatSidebarFilterTabs() {
  const { accountUuid } = useChatOverlayContext();
  const { chatHistoryFilter, setChatHistoryFilter } = useChatSidebarContext();

  return (
    <div className="flex flex-col gap-2">
      <SectionTitle>Chat History</SectionTitle>
      <SegmentedControlV2
        // All options are disabled if we do not have an active account in context
        options={[
          { label: 'Current account', value: 'current-account', disabled: isEmpty(accountUuid) },
          { label: 'All accounts', value: 'all-accounts', disabled: isEmpty(accountUuid) },
        ]}
        onChange={(value) => {
          setChatHistoryFilter(value as 'current-account' | 'all-accounts');
        }}
        // If we do not have an active account in context, show all accounts
        value={isEmpty(accountUuid) ? 'all-accounts' : chatHistoryFilter}
        activeColor="default"
        containerClassname={cn('!border-border/50 !bg-background mx-2 !h-8 self-center !rounded-full', {
          'opacity-50': isEmpty(accountUuid),
        })}
        optionClassname="!rounded-full !px-1.5 !border-transparent [&.isActive]:!bg-steel-100 !text-sm"
      />
    </div>
  );
}

/**
 * Chat history list component for the sidebar.
 * Displays grouped chat history with search and filter support.
 */
export function ChatSidebarHistory() {
  const { isMobile } = useIsMobile();
  const { chatUuid, setIsSidebarOpen, setActiveChat, setIsSettingsOpen } = useChatOverlayContext();

  const { searchResults, isLoadingChats } = useChatSidebarContext();

  const groupedChats = useMemo(() => groupChatsByDate(searchResults), [searchResults]);

  return (
    <div className="flex flex-1 flex-col gap-2 overflow-y-auto pb-24">
      {isLoadingChats && (
        <MenuItem icon={<CircleSpinnerInline />} className="text-foreground">
          Loading chats...
        </MenuItem>
      )}
      {groupedChats.map((group) => (
        <Fragment key={group.title}>
          <SectionTitle>{group.title}</SectionTitle>
          <div className="flex flex-col gap-2 px-4">
            {group.chats.map((chat) => {
              const isActive = normalizeChatUuid(chat.uuid ?? '') === normalizeChatUuid(chatUuid);
              return (
                <ChatMenuItem
                  key={chat.uuid}
                  onClick={() => {
                    setActiveChat(chat.uuid!);
                    setIsSettingsOpen(false); // Close settings when selecting a chat
                    if (isMobile) {
                      setIsSidebarOpen(false);
                    }
                  }}
                  isActive={isActive}
                  className="text-foreground"
                  hasNotifications={chat.hasUnreadMessages as boolean}
                  isInProgress={chat.activeStreamId !== null}
                >
                  {chat.title}
                </ChatMenuItem>
              );
            })}
          </div>
        </Fragment>
      ))}
    </div>
  );
}

/**
 * Settings wrapper component for the sidebar.
 * Provides a slot for apps to add their own settings components.
 */
export function ChatSidebarSettings({ children }: { children: ReactNode }) {
  return <div className="flex shrink-0 flex-col gap-3 px-4">{children}</div>;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'sticky top-0 z-10 flex items-center gap-2 px-6 text-sm font-semibold tracking-widest',
        'bg-steel-50 text-steel-300 dark:bg-steel-500 dark:text-steel-400 uppercase',
      )}
    >
      {children}
    </div>
  );
}

function MenuItem({
  children,
  icon,
  className,
  iconClassName,
  iconStyle,
  onClick,
  disabled,
  isActive,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  iconClassName?: string;
  iconStyle?: React.CSSProperties;
  onClick?: () => void;
  disabled?: boolean;
  isActive?: boolean;
}) {
  return (
    <button
      type="button"
      className={cn(
        'text-muted-foreground relative flex items-center gap-2 px-2 py-1.5 text-start',
        'hover:bg-steel-100 dark:hover:bg-steel-400 rounded-xs',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
        {
          'bg-steel-400/10 hover:bg-steel-400/10 cursor-default font-medium': isActive,
          'cursor-default hover:bg-transparent': !onClick,
        },
        className,
      )}
      onClick={isActive ? undefined : onClick}
      disabled={disabled}
    >
      {icon && (
        <div className={cn('flex size-6 shrink-0 items-center justify-center', iconClassName)} style={iconStyle}>
          {icon}
        </div>
      )}
      {children}
    </button>
  );
}

function ChatMenuItem({
  children,
  icon,
  className,
  iconClassName,
  iconStyle,
  onClick,
  disabled,
  isActive,
  hasNotifications,
  isInProgress,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  iconClassName?: string;
  iconStyle?: React.CSSProperties;
  onClick?: () => void;
  disabled?: boolean;
  isActive?: boolean;
  hasNotifications?: boolean;
  isInProgress?: boolean;
}) {
  return (
    <button
      type="button"
      className={cn(
        'text-muted-foreground relative flex items-center justify-between gap-2 px-2 py-1.5 text-start',
        'hover:bg-steel-100 dark:hover:bg-steel-400 rounded-xs',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
        {
          'bg-steel-400/10 hover:bg-steel-400/10 cursor-default font-medium': isActive,
          'cursor-default hover:bg-transparent': !onClick,
        },
        className,
      )}
      onClick={isActive ? undefined : onClick}
      disabled={disabled}
    >
      {icon && (
        <div className={cn('flex size-6 shrink-0 items-center justify-center', iconClassName)} style={iconStyle}>
          {icon}
        </div>
      )}
      {children}
      {hasNotifications && <span className="bg-rebrand-400 h-2 max-h-2 min-h-2 w-2 max-w-2 min-w-2 rounded-full" />}
      {isInProgress && !hasNotifications && <CircleSpinnerInline size={12} />}
    </button>
  );
}
